import i18n from "i18next";
import { initReactI18next } from "react-i18next";
// English is always imported eagerly: it is the fallback language and the
// smallest/most common locale. Brand and legal-en are tiny and always needed.
import en from "./locales/en.json";
import legalEn from "./locales/legal.en.json";
import brandBac from "./locales/brand.bac.json";
import brandDriveshare from "./locales/brand.driveshare.json";
import { resolveBrandText } from "@/lib/runtime-brand-copy.mjs";
import { getPlatformConfig } from "@/lib/platform/config";
import { MARKETS_BY_CODE } from "./markets";
import { isClientSsrBootstrap } from "@/lib/ssr-bootstrap";
import { usEnglishUiTranslation } from "./usEnglish";
import { PRO_OWNER_PRODUCT_NAME } from "@/lib/platform/config";
import { checkoutRecoveryCopy } from "./checkout-recovery-copy";

const usEnglishTranslation = usEnglishUiTranslation(en);

// All non-English locales are loaded lazily on demand. Each dynamic import()
// becomes its own per-language Rollup chunk (locale-fr, locale-de, …) via the
// manualChunks rule in scripts/app-chunk-strategy.mjs. A crawler fetching the
// home page only downloads the detected language's ~450 KB bundle instead of
// the full 2.9 MB for all nine languages.
// `public.<code>.json` are intentionally small public-surface overlays for
// source locales without a full app translation. They cover the public landing
// page, browsing, navigation, card labels, CTAs, and footer copy; untranslated
// application surfaces continue to resolve from English instead of implying
// a complete translation.
const LOCALE_LOADERS: Partial<
  Record<SupportedLanguage, () => Promise<[{ default: unknown }, { default: unknown }]>>
> = {
  fr: () =>
    Promise.all([import("./locales/fr.json"), import("./locales/legal.fr.json")]),
  de: () =>
    Promise.all([import("./locales/de.json"), import("./locales/legal.de.json")]),
  es: () =>
    Promise.all([import("./locales/es.json"), import("./locales/legal.es.json")]),
  it: () =>
    Promise.all([import("./locales/it.json"), import("./locales/legal.it.json")]),
  nl: () =>
    Promise.all([import("./locales/nl.json"), import("./locales/legal.nl.json")]),
  da: () =>
    Promise.all([import("./locales/da.json"), import("./locales/legal.da.json")]),
  sv: () =>
    Promise.all([import("./locales/sv.json"), import("./locales/legal.sv.json")]),
  no: () =>
    Promise.all([import("./locales/no.json"), import("./locales/legal.no.json")]),
  cs: async () => [await import("./locales/public.cs.json"), { default: legalEn }],
  pl: async () => [await import("./locales/public.pl.json"), { default: legalEn }],
  sk: async () => [await import("./locales/public.sk.json"), { default: legalEn }],
  bg: async () => [await import("./locales/public.bg.json"), { default: legalEn }],
  fi: async () => [await import("./locales/public.fi.json"), { default: legalEn }],
  "pt-BR": async () => [await import("./locales/public.pt-BR.json"), { default: legalEn }],
};

// Track which language bundles are already registered so we never double-add.
const loadedLanguages = new Set<SupportedLanguage>(["en", "en-US", "en-GB"]);

const platform = (
  import.meta.env.VITE_PLATFORM || "bac"
) as "bac" | "driveshare";

const brandResources: Record<string, Record<string, unknown>> = {
  bac: brandBac,
  driveshare: brandDriveshare,
};

// i18next can mutate registered objects during deep merges. Imported base
// bundles must stay immutable across language loads and runtime brand changes.
const cloneBrandBundle = (bundle: Record<string, unknown>): Record<string, unknown> =>
  JSON.parse(JSON.stringify(bundle));
const brand = cloneBrandBundle(brandResources[platform] || brandBac);

// Per-language brand overrides. The base `brand` bundle above is English-only;
// without these, marketing prose rendered via the brand namespace (the homepage
// hero tagline/subtitle and the "unlock your car's potential" section) would
// stay English on every non-English market. Each override holds ONLY the
// translated prose keys and is deep-merged over the English base, so any key
// it omits (brand name, support email, booking/checkout error strings) still
// falls back to English. Marketing prose is shared between brands; retain the
// translated copy when switching brands rather than resetting it to English.
type BrandOverrideLoader = () => Promise<{ default: unknown }>;
const loadPublicBrandCopy = (language: string): BrandOverrideLoader => async () => ({
  default: Object.fromEntries(Object.entries(
    (await import("./brand-public-marketing")).brandMarketingCopy[language],
  ).map(([key, value]) => [key, value.replaceAll("{{brandName}}", brandBac.name)])),
});
const bacBrandOverrides: Record<string, BrandOverrideLoader> = {
  fr: () => import("./locales/brand.bac.fr.json"),
  de: () => import("./locales/brand.bac.de.json"),
  es: () => import("./locales/brand.bac.es.json"),
  it: () => import("./locales/brand.bac.it.json"),
  nl: () => import("./locales/brand.bac.nl.json"),
  da: () => import("./locales/brand.bac.da.json"),
  sv: () => import("./locales/brand.bac.sv.json"),
  no: () => import("./locales/brand.bac.no.json"),
  cs: loadPublicBrandCopy("cs"),
  pl: loadPublicBrandCopy("pl"),
  sk: loadPublicBrandCopy("sk"),
  bg: loadPublicBrandCopy("bg"),
  fi: loadPublicBrandCopy("fi"),
  "pt-BR": loadPublicBrandCopy("pt-BR"),
};
const BRAND_OVERRIDE_LOADERS: Record<
  string,
  Partial<Record<SupportedLanguage, () => Promise<{ default: unknown }>>>
> = {
  bac: bacBrandOverrides,
  driveshare: Object.fromEntries(Object.entries(bacBrandOverrides).map(([language, loader]) => [
    language,
    async () => {
      const module = await loader();
      // These overrides contain only four marketing-prose keys, never contact
      // details, product policy or permanent URL identities.
      return { default: JSON.parse(JSON.stringify(resolveLocaleModule(module)).split(brandBac.name).join(brandResources.driveshare.name as string)) };
    },
  ])),
};

let brandOverrideLoaders = BRAND_OVERRIDE_LOADERS[platform] || {};

// ---------------------------------------------------------------------------
// Task #8034 — runtime brand cutover.
//
// `platform` above is the BUILD-TIME default. When the
// `brand_cutover_driveshare` feature flag flips at runtime,
// PlatformProvider calls applyBrandPlatform() to re-register the brand
// namespace for every already-loaded language (and future loads) with the
// other brand's bundle, including the per-language prose overrides.
// ---------------------------------------------------------------------------
let activeBrandPlatform: "bac" | "driveshare" = platform;

export async function applyBrandPlatform(
  next: "bac" | "driveshare",
): Promise<void> {
  if (next === activeBrandPlatform) return;
  activeBrandPlatform = next;
  const bundle = brandResources[next] || brandBac;
  brandOverrideLoaders = BRAND_OVERRIDE_LOADERS[next] || {};
  for (const code of loadedLanguages) {
    i18n.addResourceBundle(code, "brand", cloneBrandBundle(bundle), true, true);
    const loader = brandOverrideLoaders[code];
    if (loader) {
      try {
        const override = resolveLocaleModule(await loader());
        if (isResourceBundle(override)) {
          i18n.addResourceBundle(code, "brand", override, true, true);
        }
      } catch {
        /* English base stays — never crash a brand switch on a chunk miss */
      }
    }
  }
  // Force consumers of the brand namespace to re-render with the new copy.
  await i18n.changeLanguage(i18n.language);
}

// The canonical language list. Used for type inference, language picker UI,
// and the URL/localStorage detection logic. The resource bundles for non-en
// codes are populated lazily; the type system still treats them as valid keys.
export type SupportedLanguage =
  | "en"
  | "en-US"
  | "en-GB"
  | "en-IN"
  | "pt-BR"
  | "cs"
  | "pl"
  | "sk"
  | "bg"
  | "fi"
  | "fr"
  | "de"
  | "es"
  | "it"
  | "nl"
  | "da"
  | "sv"
  | "no";

export const SUPPORTED_LANGUAGES: Array<{
  code: SupportedLanguage;
  label: string;
  region: string;
}> = [
  { code: "en-GB", label: "English", region: "United Kingdom" },
  { code: "en-US", label: "English", region: "United States" },
  { code: "en-IN", label: "English", region: "भारत" },
  { code: "cs", label: "Čeština", region: "Česko" },
  { code: "pl", label: "Polski", region: "Polska" },
  { code: "sk", label: "Slovenčina", region: "Slovensko" },
  { code: "bg", label: "Български", region: "България" },
  { code: "fi", label: "Suomi", region: "Suomi" },
  { code: "pt-BR", label: "Português", region: "Brasil" },
  { code: "fr", label: "Français", region: "France" },
  { code: "de", label: "Deutsch", region: "Deutschland" },
  { code: "es", label: "Español", region: "España" },
  { code: "it", label: "Italiano", region: "Italia" },
  { code: "nl", label: "Nederlands", region: "Nederland" },
  { code: "da", label: "Dansk", region: "Danmark" },
  { code: "sv", label: "Svenska", region: "Sverige" },
  { code: "no", label: "Norsk", region: "Norge" },
];

const LANGUAGE_STORAGE_KEY = "bac.language";

// The supported language set at runtime — used for URL / navigator detection.
const SUPPORTED_LANG_SET = new Set<string>(
  [...SUPPORTED_LANGUAGES.map((l) => l.code.toLowerCase()), "en"],
);

/** Keep legacy en as British English without collapsing newly saved dialects. */
function canonicalSupportedLanguage(code: string): SupportedLanguage | null {
  return SUPPORTED_LANGUAGES.find((language) => language.code.toLowerCase() === code.toLowerCase())?.code ?? null;
}
export function normalizeLanguagePreference(value: string | null | undefined): SupportedLanguage | null {
  if (!value) return null;
  const normalized = value.trim().replace(/_/g, "-").toLowerCase();
  if (normalized === "en") return "en-GB";
  if (normalized === "en-us") return "en-US";
  if (normalized === "en-gb") return "en-GB";
  if (normalized === "en-in") return "en-IN";
  const exact = canonicalSupportedLanguage(normalized);
  if (exact) return exact;
  const base = normalized.split("-")[0];
  return SUPPORTED_LANG_SET.has(base) ? canonicalSupportedLanguage(base) : null;
}

function parseUrlPrefix(): { market?: string; lang?: string } {
  if (typeof window === "undefined") return {};
  const segs = window.location.pathname.split("/").filter(Boolean);
  if (segs.length === 0) return {};
  const maybeMarket = segs[0]?.toLowerCase();
  if (!maybeMarket || !(maybeMarket in MARKETS_BY_CODE)) return {};
  const maybeLang = segs[1]?.toLowerCase();
  if (maybeMarket === "in" && maybeLang === "en") {
    return { market: maybeMarket, lang: "en-IN" };
  }
  if (maybeLang && SUPPORTED_LANG_SET.has(maybeLang)) {
    return { market: maybeMarket, lang: maybeLang };
  }
  return { market: maybeMarket };
}

/**
 * Display language of a country-root URL: the explicit URL language, else the
 * market's native default. `en` maps exactly as LocaleProvider maps it
 * (en-GB for the UK, en-US elsewhere). Shared by the SSR renderer and the
 * client's first render so both agree before any preference is applied.
 */
export function countryRootDisplayLanguage(market: string, urlLanguage?: string | null): SupportedLanguage {
  const requested = urlLanguage || MARKETS_BY_CODE[market]?.defaultLanguage || "en";
  if (requested === "en") return market === "uk" ? "en-GB" : "en-US";
  return (canonicalSupportedLanguage(requested) || requested) as SupportedLanguage;
}

export function detectInitialLanguage(): SupportedLanguage {
  if (typeof window === "undefined") return "en";
  // 1. Explicit language in URL path (/{market}/{lang}/...) wins.
  const { market, lang } = parseUrlPrefix();
  // Country-root SSR documents: the URL alone decides (no stored preference).
  if (isClientSsrBootstrap() && market) {
    return countryRootDisplayLanguage(market, lang);
  }
  if (lang && SUPPORTED_LANG_SET.has(lang)) {
    return lang === "en" ? "en-GB" : canonicalSupportedLanguage(lang) || "en";
  }
  // 2. User's saved preference from a previous visit.
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const preference = normalizeLanguagePreference(stored);
    if (preference) return preference;
  } catch {
    // ignore storage errors
  }
  // 3. URL market default language (user chose a region but not a language).
  if (market) {
    const marketDefault = MARKETS_BY_CODE[market]?.defaultLanguage;
    if (marketDefault && SUPPORTED_LANG_SET.has(marketDefault.toLowerCase())) {
      return marketDefault === "en" ? (market === "uk" ? "en-GB" : "en-US") : canonicalSupportedLanguage(marketDefault) || "en";
    }
  }
  // 4. Scan the browser's ordered language preference list.
  const navLangs: string[] = Array.isArray(window.navigator?.languages)
    ? [...window.navigator.languages]
    : window.navigator?.language
      ? [window.navigator.language]
      : [];
  for (const raw of navLangs) {
    if (/^en-in\b/i.test(raw || "")) return "en-IN";
    // Browser language detection is not a saved preference: only en-GB opts
    // into UK English; plain en and other English regions use the US default.
    if (raw?.toLowerCase() === "en" || /^en-(?!gb\b)/i.test(raw || "")) return "en-US";
    const code = normalizeLanguagePreference(raw);
    if (code) return code;
  }
  return "en-US";
}

const initialLanguage = detectInitialLanguage();

// Initialise i18next with English resources only. Non-English locales are
// added asynchronously below (before React mounts for the initial language;
// on-demand for any subsequent language switch).
i18n.use({
  type: "postProcessor",
  name: "runtimeBrandCopy",
  process(value: string, keys: string[]) {
    if (keys.some(key => key === "footer.brand_transition")) return value;
    return resolveBrandText(value, getPlatformConfig().name, getPlatformConfig("bac").name);
  },
}).use(initReactI18next).init({
  postProcess: ["runtimeBrandCopy"],
  resources: {
    en: { translation: en, brand, legal: legalEn },
    "en-GB": { translation: en, brand, legal: legalEn },
    "en-US": { translation: usEnglishTranslation, brand, legal: legalEn },
    "en-IN": { translation: en, brand, legal: legalEn },
  },
  lng: initialLanguage,
  fallbackLng: "en",
  ns: ["translation", "brand", "legal"],
  defaultNS: "translation",
  interpolation: {
    defaultVariables: { proOwnerBrand: PRO_OWNER_PRODUCT_NAME },
    // SAFETY (Task #1611 audit): keeping the i18next default of
    // `escapeValue: false` is intentional. React already escapes any
    // string rendered as a JSX text node — `{t("key", { name })}` is
    // never interpreted as HTML — and double-escaping would corrupt
    // copy that legitimately contains `<`, `&`, or quotation marks
    // (e.g. brand names like "Gretsch & Co.").
    //
    // The only path that would re-introduce XSS is calling
    // `dangerouslySetInnerHTML={{ __html: t(...) }}` on a translation
    // that interpolates user input. We grep for that combination on
    // every audit pass; the two remaining `dangerouslySetInnerHTML`
    // sites in this codebase are `ui/chart.tsx` (CSS variables built
    // from a closed config object — no user input) and
    // `admin/flow-studio/EmailComposer.tsx` (the value is run through
    // DOMPurify with a strict tag/attribute allow-list immediately
    // before render). Neither involves an i18n template.
    escapeValue: false,
  },
  returnNull: false,
});

for (const [code, recovery] of Object.entries(checkoutRecoveryCopy)) {
  i18n.addResourceBundle(code, "translation", { checkout: { recovery } }, true, true);
}

/**
 * Load and register a non-English locale bundle into i18next.
 * Safe to call multiple times for the same code (idempotent via loadedLanguages).
 */
// Resolve a dynamically-imported JSON module to its bundle object. Normally the
// JSON content lives on the module's `default` export. But Rollup can serve a
// JSON module through a facade chunk whose `default` resolves to `undefined`
// (see scripts/app-chunk-strategy.mjs — this is why translation and legal each
// get their own chunk). Fall back to the module object itself so a future
// chunking regression degrades gracefully instead of crashing the whole locale
// registration with "undefined is not an object (evaluating 'n.default')".
function resolveLocaleModule(mod: unknown): unknown {
  if (mod && typeof mod === "object" && "default" in mod) {
    const resolved = (mod as { default: unknown }).default;
    if (resolved !== undefined && resolved !== null) return resolved;
  }
  return mod;
}

// A locale bundle must be a plain object for i18next to register it. A stale or
// half-fetched chunk on flaky mobile networks can resolve to `undefined`; if we
// hand that to `addResourceBundle`, i18next runs `JSON.parse(JSON.stringify(x))`
// → `JSON.parse("undefined")` and throws a SyntaxError that aborts the whole
// locale registration. Guarding here lets a bad chunk degrade to the English
// fallback instead of crashing.
function isResourceBundle(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

export async function addLanguageResources(code: SupportedLanguage): Promise<void> {
  if (code.startsWith("en") || loadedLanguages.has(code)) return;
  const loader = LOCALE_LOADERS[code];
  if (!loader) return;
  const loaded = await loader();
  const [t, l] = Array.isArray(loaded) ? loaded : [undefined, undefined];
  const translation = resolveLocaleModule(t);
  // If the core translation bundle is unusable, bail without marking the
  // language as loaded so a later navigation can retry; English keeps showing
  // until then. Never call addResourceBundle with a non-object.
  if (!isResourceBundle(translation)) return;
  i18n.addResourceBundle(
    code,
    "translation",
    translation,
    true,
    true,
  );
  i18n.addResourceBundle(
    code,
    "brand",
    // Runtime-cutover aware: register whichever brand is CURRENTLY active
    // (task #8034), not the build-time capture, so a language loaded after
    // a cutover doesn't resurrect the old brand copy.
    cloneBrandBundle(brandResources[activeBrandPlatform] || brandBac),
    true,
    true,
  );
  // Overlay the per-language brand prose on top of the English base so the
  // homepage marketing copy resolves in the market's language; omitted keys
  // keep falling back to the English bundle registered just above.
  const brandOverrideLoader = brandOverrideLoaders[code];
  if (brandOverrideLoader) {
    const override = resolveLocaleModule(await brandOverrideLoader());
    if (isResourceBundle(override)) {
      i18n.addResourceBundle(
        code,
        "brand",
        override,
        true,
        true,
      );
    }
  }
  const legal = resolveLocaleModule(l);
  if (isResourceBundle(legal)) {
    i18n.addResourceBundle(
      code,
      "legal",
      legal,
      true,
      true,
    );
  }
  loadedLanguages.add(code);
}

/**
 * Task #6961 — outcome of the pre-mount locale-bundle load. main.tsx keys the
 * stale-chunk recovery + telemetry off this instead of a bare void promise.
 */
export type I18nReadyOutcome =
  | { ok: true }
  | { ok: false; reason: "timeout" | "error"; language: SupportedLanguage; error?: unknown };

/**
 * Upper bound on how long the app is allowed to stay unmounted waiting for a
 * non-English locale chunk. Past this, we mount anyway (English fallback via
 * i18next's fallbackLng) and upgrade in place when/if the chunk arrives.
 */
export const I18N_READY_TIMEOUT_MS = 2_500;

async function loadInitialLanguage(): Promise<void> {
  await addLanguageResources(initialLanguage);
  await i18n.changeLanguage(initialLanguage);
}

/**
 * Race a locale-bundle load against a timeout WITHOUT ever rejecting.
 * Exported for unit tests.
 *
 * - resolved in time  → { ok: true } (no flash-of-English, unchanged behavior)
 * - timed out         → { ok: false, reason: "timeout" }; the in-flight load
 *   keeps running and upgrades the UI in place when it finishes
 *   (addResourceBundle + changeLanguage re-render via react-i18next).
 * - rejected          → { ok: false, reason: "error" }; caller decides whether
 *   to run the one-shot stale-chunk reload or mount with English.
 */
export async function raceInitialLocaleLoad(
  load: Promise<void>,
  timeoutMs: number,
  language: SupportedLanguage,
): Promise<I18nReadyOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      load.then(
        () => ({ ok: true }) as const,
        (error) => ({ ok: false, reason: "error", language, error }) as const,
      ),
      new Promise<I18nReadyOutcome>((resolve) => {
        timer = setTimeout(
          () => resolve({ ok: false, reason: "timeout", language }),
          timeoutMs,
        );
      }),
    ]);
    if (!result.ok && result.reason === "timeout") {
      // Keep the slow load running in the background; when it eventually
      // resolves, changeLanguage() re-renders the app in the right language.
      // Swallow a late rejection so it never surfaces as unhandled.
      load.catch(() => {});
    }
    return result;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Resolves once the initial language bundle is registered — or FAILS OPEN
 * after a bounded wait so React always mounts (Task #6961: blank first load
 * from email/SMS links when the locale chunk stalls or 404s post-deploy).
 *
 * main.tsx awaits this before calling createRoot(); on the happy path the
 * behavior is unchanged (no flash-of-English for non-English users; English
 * users resolve in the next microtask). This promise NEVER rejects.
 */
export const i18nReady: Promise<I18nReadyOutcome> = (async () => {
  if (initialLanguage.startsWith("en")) return { ok: true } as const;
  const outcome = await raceInitialLocaleLoad(
    loadInitialLanguage(),
    I18N_READY_TIMEOUT_MS,
    initialLanguage,
  );
  if (!outcome.ok && outcome.reason === "error") {
    // Background retry: a transient network blip on a cold mobile tap can
    // clear within seconds. One delayed re-attempt upgrades the UI in place;
    // a persistent failure (e.g. stale chunk) is handled by main.tsx's
    // one-shot reload recovery instead.
    setTimeout(() => {
      loadInitialLanguage().catch(() => {});
    }, 3_000);
  }
  return outcome;
})();

export async function setLanguage(code: SupportedLanguage): Promise<void> {
  if (!SUPPORTED_LANG_SET.has(code.toLowerCase()) && code !== "en") return;
  const selected = code === "en" ? "en-GB" : code;
  await applyDisplayLanguage(selected);
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, selected);
      document.documentElement.lang = selected;
    }
  } catch {
    // ignore storage errors
  }
}

/** URL-owned display changes must not replace an explicit saved preference. */
export async function applyDisplayLanguage(code: SupportedLanguage): Promise<void> {
  await addLanguageResources(code);
  await i18n.changeLanguage(code);
  if (typeof document !== "undefined") document.documentElement.lang = code;
}

if (typeof document !== "undefined") {
  document.documentElement.lang = initialLanguage;
}

export { platform, LANGUAGE_STORAGE_KEY };
export default i18n;
